import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CustomButton from "./custom-button";
import React, { FunctionComponent } from "react";
import { saveAs } from "file-saver";
import { downloadSecureFile } from "../../api/files";

/**
 * Interface defining the props for the DownloadFileButton component.
 * 
 * @interface DownloadFileButtonProps
 * @property {string} label - The text to display on the button
 * @property {string} filePath - The URL or path to the file to download
 * @property {string} fileName - The name to give the downloaded file
 */
interface DownloadFileButtonProps {
  label: string;
  filePath: string;
  fileName: string;
  useSecureDownload?: boolean; // New prop to enable secure download
}

/**
 * A reusable button component for downloading files.
 * 
 * This component provides a button that:
 * - Downloads a file from a specified path
 * - Handles network errors and empty file cases
 * - Uses the file-saver library for client-side downloads
 * - Displays a cloud download icon
 * 
 * @component
 * @example
 * ```tsx
 * <DownloadFileButton
 *   label="Download Report"
 *   filePath="/api/reports/123"
 *   fileName="report.pdf"
 * />
 * ```
 * 
 * @remarks
 * - Uses Material-UI's CloudDownloadIcon
 * - Implements error handling for network requests
 * - Validates file size before downloading
 * - Uses the file-saver library for cross-browser compatibility
 * 
 * @returns {JSX.Element} A button component that triggers file downloads
 */
const DownloadFileButton: FunctionComponent<DownloadFileButtonProps> = (
  props
) => {
  const { label, filePath, fileName, useSecureDownload = false } = props;
  
  const handleButtonClick = async () => {
    try {
      if (useSecureDownload || (!filePath.startsWith('http') && !filePath.startsWith('/'))) {
        // Use secure download for private files
        await downloadSecureFile(filePath, fileName);
      } else {
        // Use traditional fetch for public files
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error(
            `Network response was not ok, status: ${response.status}`
          );
        }
        const blob = await response.blob();
        if (blob.size > 0) {
          saveAs(blob, fileName);
        } else {
          console.error("The downloaded file is empty.");
        }
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };
  return (
    <>
      <CustomButton
        label={label}
        startIcon={<CloudDownloadIcon />}
        handleOnClick={handleButtonClick}
      />
    </>
  );
};

export default DownloadFileButton;
